import { useCallback, useEffect, useRef, useState } from "react"
import { Plus, Trash2 } from "lucide-react"
import { motion } from "framer-motion"
import type { CatalogMaterial, SelectedMaterial } from "../../../../types/calculadoraDeMateriales.ts"
import {
  fetchCategoryMaterialsPage,
  searchItemToCatalogMaterial,
} from "../../../../services/catalogMaterialsService.ts"
import { formatMaterialUnitMeasureLabel, ulVariants } from "../../../../constants/calculadoraDeMateriales.ts"
import styles from "../CalculadoraDeMateriales.module.scss"

const PAGE_SIZE = 50

type CalculadoraExpandedMaterialsListProps = {
  categoryId: string
  categoryName: string
  selectedMaterials: SelectedMaterial[]
  isEstimacionDeCostos: boolean
  toggleMaterial: (material: CatalogMaterial, categoryId: string) => void
  onCreateCustomMaterialFromList: () => void
  onOpenMaterialDetail: (materialId: number | string) => void
}

function resolveHasMore(
  res: { page: number; pageSize: number; total: number; totalPages: number; items: unknown[] }
): boolean {
  if (res.totalPages > 0) {
    return res.page < res.totalPages
  }
  if (res.total > 0) {
    return res.page * res.pageSize < res.total
  }
  return res.items.length >= res.pageSize
}

const CalculadoraExpandedMaterialsList = ({
  categoryId,
  categoryName,
  selectedMaterials,
  isEstimacionDeCostos,
  toggleMaterial,
  onCreateCustomMaterialFromList,
  onOpenMaterialDetail,
}: CalculadoraExpandedMaterialsListProps) => {
  const [items, setItems] = useState<CatalogMaterial[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState("")
  const sentinelRef = useRef<HTMLLIElement>(null)
  const loadingMoreRef = useRef(false)

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (loadingMoreRef.current) return
      if (append) {
        setLoadingMore(true)
        loadingMoreRef.current = true
      } else {
        setLoading(true)
        setError("")
      }
      try {
        const res = await fetchCategoryMaterialsPage(categoryId, {
          page: nextPage,
          pageSize: PAGE_SIZE,
        })
        const mapped = res.items.map((row) => searchItemToCatalogMaterial(row))
        setItems((prev) => (append ? [...prev, ...mapped] : mapped))
        setPage(res.page)
        setHasMore(resolveHasMore({ ...res, items: res.items }))
      } catch {
        if (!append) {
          setItems([])
          setHasMore(false)
          setError("No se pudieron cargar los materiales de esta categoría.")
        }
      } finally {
        setLoading(false)
        setLoadingMore(false)
        loadingMoreRef.current = false
      }
    },
    [categoryId]
  )

  useEffect(() => {
    setItems([])
    setPage(1)
    setHasMore(false)
    loadingMoreRef.current = false
    void loadPage(1, false)
  }, [categoryId, loadPage])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || loading) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        if (loadingMoreRef.current || loading || !hasMore) return
        void loadPage(page + 1, true)
      },
      { root: null, rootMargin: "240px 0px 120px 0px", threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loading, page, loadPage, items.length])

  return (
    <motion.ul
      variants={ulVariants}
      initial="hidden"
      animate="visible"
      className={styles.container_body_categoryMaterials_list}
    >
      <li
        className={`${styles.container_body_categoryMaterials_list_item} ${styles.customMaterialListItem}`}
        onClick={onCreateCustomMaterialFromList}
        style={{ cursor: "pointer" }}
      >
        <div className={styles.container_body_categoryMaterials_list_item_text}>
          <span className={styles.container_body_categoryMaterials_list_item_material}>Crear material personalizado</span>
          <span className={styles.container_body_categoryMaterials_list_item_category}>
            Agrega un material que no esté en la lista
          </span>
        </div>
        <div className={styles.container_body_categoryMaterials_list_item_buttonContainer}>
          <button
            type="button"
            className={`${styles.container_body_categoryMaterials_list_item_button} ${styles.customMaterialButton}`}
          >
            Crear
          </button>
        </div>
      </li>
      {loading && items.length === 0 ? (
        <li className={styles.container_body_categoryMaterials_list_item}>
          <span className={styles.container_body_categoryMaterials_list_item_material}>Cargando materiales…</span>
        </li>
      ) : null}
      {error && items.length === 0 ? (
        <li className={styles.container_body_categoryMaterials_list_item}>
          <span className={styles.container_body_categoryMaterials_list_item_material}>{error}</span>
        </li>
      ) : null}
      {items.map((material) => {
        const isChecked = selectedMaterials.some((m) => m.id === material.id)
        const unitLabel = formatMaterialUnitMeasureLabel(material.unit)
        return (
          <li key={material.id} className={styles.container_body_categoryMaterials_list_item}>
            <div className={styles.container_body_categoryMaterials_list_item_main}>
              <span className={styles.container_body_categoryMaterials_list_item_material}>{material.name}</span>
              <span className={styles.container_body_categoryMaterials_list_item_category}>{categoryName}</span>
              {unitLabel ? (
                <p className={styles.container_body_categoryMaterials_list_item_unitLine}>{unitLabel}</p>
              ) : null}
              <button
                type="button"
                className={styles.container_body_categoryMaterials_list_item_detailLink}
                onClick={() => onOpenMaterialDetail(material.id)}
              >
                Ver detalle
              </button>
            </div>
            <div
              className={`${styles.container_body_categoryMaterials_list_item_buttonContainer} 
                            ${!isEstimacionDeCostos && styles.container_body_categoryMaterials_list_item_buttonContainer_estimacionDeCostos}`}
            >
              {isEstimacionDeCostos && material.price != null ? (
                <span className={styles.container_body_categoryMaterials_list_item_priceBadge}>
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(Number(material.price))}
                </span>
              ) : null}
              <button
                type="button"
                className={styles.container_body_categoryMaterials_list_item_button}
                onClick={() => toggleMaterial(material, categoryId)}
                title={isChecked ? "Quitar de la lista" : "Agregar a la lista"}
                aria-label={
                  isChecked ? `Quitar ${material.name} de la lista` : `Agregar ${material.name} a la lista`
                }
              >
                {isChecked ? <Trash2 size={18} /> : <Plus size={18} />}
              </button>
            </div>
          </li>
        )
      })}
      {loadingMore ? (
        <li className={styles.container_body_categoryMaterials_list_item}>
          <span className={styles.container_body_categoryMaterials_list_item_material}>Cargando más…</span>
        </li>
      ) : null}
      {hasMore && !loading ? (
        <li
          ref={sentinelRef}
          className={styles.container_body_categoryMaterials_list_sentinel}
          aria-hidden
        />
      ) : null}
    </motion.ul>
  )
}

export default CalculadoraExpandedMaterialsList
